import { useEffect, useRef, useState } from "react";

const ANIM_SETTINGS_KEY = "hnzw_animation_settings_v1";

export const DEFAULT_ANIM_SETTINGS = {
  // 滚动动画
  enabled: true,
  speed: "normal",
  style: "fadeInUp",
  // 视觉特效
  parallax: false,
  particle: false,
  tilt3D: false,
  floatIcons: true,
  ripple: true,
  stagger: true,
  counter: true,
  shimmer: false,
  hoverScale: false,
  glowPulse: false,
  smoothScroll: true,
  // 强度
  intensity: "subtle",
};

export function readAnimSettings() {
  try {
    const raw = localStorage.getItem(ANIM_SETTINGS_KEY);
    return raw ? { ...DEFAULT_ANIM_SETTINGS, ...JSON.parse(raw) } : DEFAULT_ANIM_SETTINGS;
  } catch {
    return DEFAULT_ANIM_SETTINGS;
  }
}

export function saveAnimSettings(settings) {
  try {
    localStorage.setItem(ANIM_SETTINGS_KEY, JSON.stringify(settings));
  } catch { /* silent */ }
}

const speedMap = { slow: 1.6, normal: 1, fast: 0.5 };
const intensityMap = { subtle: 0.4, medium: 1, dramatic: 1.6 };

export function useScrollAnimation(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const settings = readAnimSettings();

  useEffect(() => {
    if (!settings.enabled) { setVisible(true); return; }
    const el = ref.current;
    if (!el) return;
    const speed = speedMap[settings.speed] || 1;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), (options.delay || 0) * speed);
          observer.unobserve(el);
        }
      },
      { threshold: options.threshold || 0.12, rootMargin: options.rootMargin || "0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [settings.enabled, settings.speed, options.delay, options.threshold, options.rootMargin]);

  const animClass = settings.enabled
    ? `anim-${settings.style} ${visible ? "anim-visible" : "anim-hidden"} anim-speed-${settings.speed} anim-intensity-${settings.intensity}`
    : "";

  return [ref, animClass, visible];
}

/* ===== Animated Counter ===== */
export function useAnimatedCounter(end, duration = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  const settings = readAnimSettings();

  useEffect(() => {
    if (!settings.counter) { setCount(end); return; }
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) { setStarted(true); observer.unobserve(el); }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started, settings.counter]);

  useEffect(() => {
    if (!started || !settings.counter) return;
    let start = 0;
    const step = Math.ceil(end / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [started, end, duration, settings.counter]);

  return [ref, count];
}

/* ===== Stagger Children ===== */
export function useStaggerChildren(count, baseDelay = 80) {
  const settings = readAnimSettings();
  if (!settings.stagger) return Array(count).fill(0);
  const intensity = intensityMap[settings.intensity] || 1;
  return Array.from({ length: count }, (_, i) => i * baseDelay * intensity);
}

/* ===== Ripple Effect Handler ===== */
export function createRipple(e) {
  const settings = readAnimSettings();
  if (!settings.ripple) return;
  const btn = e.currentTarget;
  const rect = btn.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height) * 2;
  const x = e.clientX - rect.left - size / 2;
  const y = e.clientY - rect.top - size / 2;
  const ripple = document.createElement("span");
  ripple.className = "ripple-effect";
  ripple.style.cssText = `left:${x}px;top:${y}px;width:${size}px;height:${size}px`;
  btn.appendChild(ripple);
  ripple.addEventListener("animationend", () => ripple.remove());
}

/* ===== 3D Tilt Hook ===== */
export function useTilt() {
  const ref = useRef(null);
  const settings = readAnimSettings();

  useEffect(() => {
    if (!settings.tilt3D) return;
    const el = ref.current;
    if (!el) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      const intensity = intensityMap[settings.intensity] || 1;
      el.style.transform = `perspective(800px) rotateY(${x * 8 * intensity}deg) rotateX(${-y * 6 * intensity}deg) translateZ(4px)`;
    };
    const onLeave = () => { el.style.transform = "perspective(800px) rotateY(0deg) rotateX(0deg) translateZ(0)"; };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => { el.removeEventListener("mousemove", onMove); el.removeEventListener("mouseleave", onLeave); };
  }, [settings.tilt3D, settings.intensity]);

  return ref;
}

/* ===== Glow Pulse ===== */
export function useGlowPulse() {
  const settings = readAnimSettings();
  return settings.glowPulse ? "glow-pulse" : "";
}
